
import React, { type ReactNode, createContext, useContext, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useNotifications } from './NotificationContext';
import { useOrganization } from '../hooks/useOrganization';
import { useAuth } from './AuthContext';
import {
    mapMatchRow, MATCH_ROW_SELECT, isTerminalTaskStatus,
    type MatchResult, type MatchingRun,
} from '../lib/mappers/match.mapper';
import { notifyQuotaChanged, openUpgradeModal, parseQuotaExceeded, quotaExceededMessage } from '../lib/quotaErrors';

// ─── Endpoints ───────────────────────────────────────────────────────────────
// Sur Vercel Production : seules les variables sans suffixe sont définies (pointent vers la prod).
// Sur Vercel Preview/Development : seules les variables _REC sont définies (pointent vers les fonctions -rec).
// Le fallback permet au même code de fonctionner dans les deux environnements.
const CV_URL      = import.meta.env.VITE_EDGE_CREATE_CV      ?? import.meta.env.VITE_EDGE_CREATE_CV_REC      ?? '';
const MISSION_URL = import.meta.env.VITE_EDGE_CREATE_MISSION ?? import.meta.env.VITE_EDGE_CREATE_MISSION_REC ?? '';
const MATCHING_URL = import.meta.env.VITE_EDGE_MATCHING      ?? import.meta.env.VITE_EDGE_MATCHING_REC       ?? '';
const MAX_FILE_SIZE_BYTES  = 10 * 1024 * 1024;

/** Vrai quand l'appli parle au moteur de RECETTE (matching-init-rec).
 *  Déduit de l'endpoint réellement résolu, et non d'une variable dédiée : c'est
 *  la seule source qui ne peut pas mentir sur le moteur réellement appelé.
 *  Sert à n'exposer les réglages d'expérimentation qu'en recette. */
export const IS_REC_ENGINE = /matching-init-rec/.test(MATCHING_URL);

/** Message de variable d'endpoint manquante — même formulation que MissionRegistryView.
 *  La chaîne vide compte comme une absence : `??` ne rattrape que null/undefined, donc
 *  une variable définie mais vide traverse le fallback, et `fetch('')` résout contre le
 *  document courant — le rewrite SPA de vercel.json renvoie alors 200 text/html, `resp.ok`
 *  est vrai, et le polling réafficherait les matchs de l'analyse précédente comme neufs. */
const missingEndpointMessage = (name: string) =>
    `Variable ${name} / _REC manquante — vérifier Vercel env vars`;

const THRESHOLD_MAP: Record<'strict' | 'balanced' | 'open', number> = {
    strict: 0.7, balanced: 0.5, open: 0.3,
};

async function fetchWithTimeout(url: string, options: RequestInit, ms = 30000): Promise<Response> {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ms);
    try {
        return await fetch(url, { ...options, signal: ctrl.signal });
    } catch (err) {
        if ((err as Error).name === 'AbortError') throw new Error('TIMEOUT');
        throw err;
    } finally {
        clearTimeout(timer);
    }
}

// P0.6 (audit M7) — types et mapping centralisés dans lib/mappers/match.mapper.ts.
// Ré-exportés ici pour ne pas casser les imports existants.
export type {
    CriterionResult, CriterionStatus, ObjectionItem, VerificationQuestion,
    OutreachContent, ScoreBreakdown, MatchResult, MatchingRun,
} from '../lib/mappers/match.mapper';

export interface RunMatchingParams {
    missionTab: 'database' | 'file';
    jdFiles: File[];
    missionText: string;
    selectedMissionId: string;
    missionName: string;
    cvFiles: File[];
    candidateTab: 'all' | 'new' | 'existing';
    candidateMode: 'all' | 'my' | 'selection';
    selectedCandidateIds: string[];
    matchingMode: 'strict' | 'balanced' | 'open';
    topK: number;
    /** MATCH-JUDGE-1 — juge sur compétences ancrées (niveaux 0-10).
     *  Laisser `undefined` = ne rien envoyer, le moteur applique son propre défaut
     *  (activé en recette, désactivé en production). Ne forcer que pour comparer. */
    useScoredJudge?: boolean;
}

interface MatchingContextType {
    viewState: 'setup' | 'submitted';
    matchResults: MatchResult[];
    isSubmitting: boolean;
    submitStep: string;
    submitProgress: number;
    submitError: string | null;
    submittedMissionId: number | null;
    submittedMissionSourceId: string;
    submittedMatchingMode: 'strict' | 'balanced' | 'open';
    submittedTaskId: number | null;
    missionHistory: MatchingRun[];
    isLiveUpdating: boolean;
    runMatching: (params: RunMatchingParams) => Promise<void>;
    loadFromTask: (taskId: number) => Promise<void>;
    loadMissionHistory: (missionId: number) => Promise<void>;
    reset: () => void;
    clearError: () => void;
}

const MatchingContext = createContext<MatchingContextType | undefined>(undefined);

export const MatchingProvider = ({ children }: { children: ReactNode }) => {
    const navigate = useNavigate();
    const { notify } = useNotifications();
    const { org } = useOrganization();
    const { user } = useAuth();

    const [viewState, setViewState]                     = useState<'setup' | 'submitted'>('setup');
    const [matchResults, setMatchResults]               = useState<MatchResult[]>([]);
    const [isSubmitting, setIsSubmitting]               = useState(false);
    const [submitStep, setSubmitStep]                   = useState('');
    const [submitProgress, setSubmitProgress]           = useState(0);
    const [submitError, setSubmitError]                 = useState<string | null>(null);
    const [submittedMissionId, setSubmittedMissionId]   = useState<number | null>(null);
    const [submittedMissionSourceId, setSubmittedMissionSourceId] = useState('');
    const [submittedMatchingMode, setSubmittedMatchingMode] = useState<'strict' | 'balanced' | 'open'>('balanced');
    const [submittedTaskId, setSubmittedTaskId]         = useState<number | null>(null);
    const [missionHistory, setMissionHistory]           = useState<MatchingRun[]>([]);
    const [isLiveUpdating, setIsLiveUpdating]           = useState(false);

    const isSubmittingRef = useRef(false);
    const channelRef      = useRef<RealtimeChannel | null>(null);
    const pollRef         = useRef<ReturnType<typeof setInterval> | null>(null);
    // Ref to always capture the latest org value inside callbacks (avoids stale closure)
    const orgRef          = useRef(org);
    orgRef.current        = org;

    const clearPoll = useCallback(() => {
        if (pollRef.current !== null) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    // ── Cleanup Realtime subscription ────────────────────────────────────────
    const cleanupChannel = useCallback(() => {
        if (channelRef.current) {
            supabase.removeChannel(channelRef.current).catch(() => {});
            channelRef.current = null;
        }
    }, []);

    // P0.6 — mapping délégué au mapper central (lib/mappers/match.mapper.ts)

    // 429 quota (missions ou matchings du mois) : le message nomme le quota atteint
    // et la notification propose la montée en gamme — jamais un « erreur 429 » brut.
    const raiseIfQuotaExceeded = useCallback(async (resp: Response): Promise<void> => {
        if (resp.status !== 429) return;
        const body = await resp.json().catch(() => null);
        const info = parseQuotaExceeded(resp.status, body);
        if (!info) return;
        const msg = quotaExceededMessage(info);
        notify('error', 'Quota atteint', {
            description: msg,
            action: { label: 'Voir les forfaits', onClick: openUpgradeModal },
        });
        throw new Error(msg);
    }, [notify]);

    const loadMissionHistory = useCallback(async (missionId: number) => {
        try {
            const { data: tasks } = await supabase
                .from('matching_tasks')
                .select('id, created_at, status')
                .eq('mission_id', missionId)
                .order('created_at', { ascending: false });
            if (!tasks?.length) return;

            const runs = await Promise.all(tasks.map(async (task: any) => {
                const { data: matchData } = await (supabase as any)
                    .from('matches')
                    .select(MATCH_ROW_SELECT)
                    .eq('matching_task_id', task.id)
                    .order('match_score', { ascending: false })
                    .limit(50);
                return {
                    task_id: task.id,
                    created_at: task.created_at,
                    matched_count: matchData?.length ?? 0,
                    candidates: (matchData || []).map(mapMatchRow),
                    status: task.status,
                } as MatchingRun;
            }));
            setMissionHistory(runs);
        } catch {
            // Ignore history refresh errors; the live matching flow has its own error handling.
        }
    }, []);

    const runMatching = useCallback(async (params: RunMatchingParams) => {
        if (isSubmittingRef.current) return;

        // Client-side file size validation
        const oversizedCv = params.cvFiles.find(f => f.size > MAX_FILE_SIZE_BYTES);
        if (oversizedCv) {
            setSubmitError(`Fichier trop lourd (max 10 Mo) : ${oversizedCv.name}`);
            return;
        }
        if (params.jdFiles.length > 0 && params.jdFiles[0].size > MAX_FILE_SIZE_BYTES) {
            setSubmitError('Fichier de mission trop lourd (max 10 Mo)');
            return;
        }

        // Endpoints : on refuse de partir plutôt que d'appeler une URL vide, qui
        // répondrait 200 text/html (rewrite SPA) et ferait passer les résultats
        // d'une analyse précédente pour ceux qu'on vient de calculer.
        if (!MATCHING_URL) {
            setSubmitError(missingEndpointMessage('VITE_EDGE_MATCHING'));
            return;
        }
        if (params.missionTab !== 'database' && !MISSION_URL) {
            setSubmitError(missingEndpointMessage('VITE_EDGE_CREATE_MISSION'));
            return;
        }
        if (params.cvFiles.length > 0 && !CV_URL) {
            setSubmitError(missingEndpointMessage('VITE_EDGE_CREATE_CV'));
            return;
        }

        isSubmittingRef.current = true;
        setIsSubmitting(true);
        setSubmitError(null);
        setSubmitProgress(0);
        setMatchResults([]);
        setSubmittedMatchingMode(params.matchingMode);
        setSubmittedMissionSourceId(params.selectedMissionId);
        setSubmittedTaskId(null);

        try {

            // ── Step 1: Ensure we have a mission_id ──────────────────────────
            let missionId: number;
            if (params.missionTab === 'database') {
                setSubmitStep('Préparation de la mission...');
                setSubmitProgress(3);
                missionId = parseInt(params.selectedMissionId, 10);
            } else {
                setSubmitStep('Création de la mission...');
                setSubmitProgress(3);
                const { data: { session: missionSession } } = await supabase.auth.getSession();
                if (!missionSession?.access_token) {
                    throw new Error('Session expirée. Veuillez vous déconnecter puis reconnecter.');
                }
                const missionHeaders = {
                    'Authorization': `Bearer ${missionSession.access_token}`,
                    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
                };

                let missionResp: Response;
                if (params.jdFiles.length > 0) {
                    const fd = new FormData();
                    fd.append('file', params.jdFiles[0]);
                    if (org?.id) fd.append('organization_id', org.id);
                    if (user?.id) fd.append('created_by', user.id);
                    missionResp = await fetchWithTimeout(MISSION_URL, {
                        method: 'POST',
                        body: fd,
                        headers: missionHeaders
                    });
                } else {
                    missionResp = await fetchWithTimeout(MISSION_URL, {
                        method: 'POST',
                        headers: {
                            ...missionHeaders,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ text: params.missionText, organization_id: org?.id, created_by: user?.id }),
                    });
                }
                if (!missionResp.ok) {
                    await raiseIfQuotaExceeded(missionResp);
                    throw new Error(`Erreur création mission (${missionResp.status})`);
                }
                const data = await missionResp.json();
                missionId = data.mission_id;
                notifyQuotaChanged();
            }

            // ── Step 2: Upload new CV files ──────────────────────────────────
            const newCandidateIds: number[] = [];
            for (let i = 0; i < params.cvFiles.length; i++) {
                setSubmitStep(`Upload CV ${i + 1}/${params.cvFiles.length}...`);
                setSubmitProgress(3 + Math.round(6 * (i + 1) / params.cvFiles.length));
                const fd = new FormData();
                fd.append('file', params.cvFiles[i]);
                if (org?.id) fd.append('organization_id', org.id);
                if (user?.id) fd.append('created_by', user.id);
                const { data: { session: cvSession } } = await supabase.auth.getSession();
                if (!cvSession?.access_token) {
                    throw new Error('Session expirée. Veuillez vous déconnecter puis reconnecter.');
                }
                const resp = await fetchWithTimeout(CV_URL, {
                    method: 'POST',
                    body: fd,
                    headers: {
                        'Authorization': `Bearer ${cvSession.access_token}`,
                        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
                    }
                });
                if (!resp.ok) {
                    await raiseIfQuotaExceeded(resp);
                    throw new Error(`Erreur upload CV ${i + 1} (${resp.status})`);
                }
                const data = await resp.json();
                newCandidateIds.push(data.candidate_id);
            }
            if (params.cvFiles.length > 0) notifyQuotaChanged();

            // ── Step 3: Subscribe to Supabase Realtime BEFORE firing matching ────
            // Progress will be driven by real matching_tasks updates
            setSubmitStep('Connexion au flux temps réel...');
            setSubmitProgress(10);
            cleanupChannel();

            let localTaskId: number | null = null;

            await new Promise<void>((resolve) => {
                const connectTimeout = setTimeout(() => {
                    // If subscription doesn't connect in 8s, proceed anyway (graceful degradation)
                    resolve();
                }, 8000);

                const channel = supabase
                    .channel(`matching-${missionId}-${Date.now()}`)
                    .on(
                        'postgres_changes',
                        {
                            event: '*',
                            schema: 'public',
                            table: 'matching_tasks',
                            filter: `mission_id=eq.${missionId}`,
                        },
                        (payload) => {
                            const row = (payload.new ?? (payload as any).record) as Record<string, unknown>;
                            if (!row) return;
                            const taskProgress = typeof row.progress === 'number' ? row.progress : 0;
                            const taskStatus   = typeof row.status   === 'string' ? row.status   : '';
                            const taskId       = typeof row.id       === 'number' ? row.id       : null;
                            if (taskId) { localTaskId = taskId; setSubmittedTaskId(taskId); }
                            if (taskProgress > 0) setSubmitProgress(taskProgress);
                            if (taskStatus)      setSubmitStep(taskStatus);
                        }
                    )
                    .subscribe((status) => {
                        if (status === 'SUBSCRIBED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
                            clearTimeout(connectTimeout);
                            resolve();
                        }
                    });

                channelRef.current = channel;
            });

            // ── Step 3b: Polling fallback for matching_tasks progress ────────
            // Runs every 3s in parallel with Realtime — covers Realtime glitches.
            let lastPolledTaskId: number | null = null;
            pollRef.current = setInterval(async () => {
                try {
                    const { data } = await supabase
                        .from('matching_tasks')
                        .select('id, progress, status')
                        .eq('mission_id', missionId)
                        .order('created_at', { ascending: false })
                        .limit(1)
                        .maybeSingle();
                    if (!data) return;
                    if (data.id && lastPolledTaskId !== data.id) {
                        lastPolledTaskId = data.id;
                        if (!localTaskId) { localTaskId = data.id; setSubmittedTaskId(data.id); }
                    }
                    if (typeof data.progress === 'number' && data.progress > 0) {
                        setSubmitProgress(p => data.progress > p ? data.progress : p);
                    }
                    if (typeof data.status === 'string' && data.status) {
                        setSubmitStep(data.status);
                    }
                } catch { /* ignore poll errors */ }
            }, 3000);

            // ── Step 4: Fire matching ────────────────────────────────────────
            setSubmitStep('Lancement de l\'analyse...');
            setSubmitProgress(15);
            const allCandidateIds = params.candidateTab === 'all'
                ? []
                : [
                    ...newCandidateIds,
                    ...params.selectedCandidateIds.map(Number).filter(n => !isNaN(n)),
                ];
            const threshold = THRESHOLD_MAP[params.matchingMode];
            const payload: Record<string, unknown> = { mission_id: missionId, candidate_ids: allCandidateIds, candidate_mode: params.candidateMode, threshold, top_k: params.topK, organization_id: orgRef.current?.id, user_id: user?.id };
            // Envoyé UNIQUEMENT si l'appelant tranche explicitement : sinon le moteur
            // garde son défaut, qui diffère entre recette et production.
            if (params.useScoredJudge !== undefined) payload.use_scored_judge = params.useScoredJudge;
            console.log('[Matching] payload →', payload, '| endpoint →', MATCHING_URL);

            // The HTTP response is our final completion signal (especially for "no_match" case,
            // which skips the progress=100 update in matching_tasks).
            let resp: Response;
            const { data: { session: currentSession } } = await supabase.auth.getSession();
            if (!currentSession?.access_token) {
                throw new Error('Session expirée. Veuillez vous déconnecter puis reconnecter.');
            }
            try {
                resp = await fetchWithTimeout(MATCHING_URL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${currentSession.access_token}`,
                        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
                    },
                    body: JSON.stringify(payload),
                }, 120000);
            } finally {
                clearPoll();
                cleanupChannel();
            }

            if (!resp.ok) {
                await raiseIfQuotaExceeded(resp);
                let detail = '';
                try { const body = await resp.json(); detail = body?.message ? ` — ${body.message}` : ''; } catch { /* ignore */ }
                throw new Error(`Erreur lancement matching (${resp.status})${detail}`);
            }

            // Le matching est décompté à la mise en queue, côté serveur : la
            // réponse reçue, le compteur est à jour et l'affichage peut se relire.
            notifyQuotaChanged();

            // ── Step 5: Poll Supabase until results appear ───────────────────
            // Edge function responds immediately (async mode) — results arrive later.
            // We animate progress slowly and wait up to 3 minutes for matches.
            setSubmitProgress(p => Math.max(p, 60));
            setSubmitStep('Analyse IA en cours...');

            try { await resp.json(); } catch { /* ignore body */ }

            const MAX_RESULTS_WAIT = 180_000;
            const RESULTS_POLL_MS  = 3_000;
            const resultsStart = Date.now();
            const progressTick = setInterval(
                () => setSubmitProgress(p => p < 99 ? p + 1 : 99),
                2500,
            );

            let results: MatchResult[] = [];
            try {
                while (Date.now() - resultsStart < MAX_RESULTS_WAIT) {
                    // Resolve taskId if realtime/poll didn't catch it yet
                    if (!localTaskId) {
                        const { data: taskRow } = await supabase
                            .from('matching_tasks')
                            .select('id, progress, status')
                            .eq('mission_id', missionId)
                            .order('created_at', { ascending: false })
                            .limit(1)
                            .maybeSingle();
                        if (taskRow?.id) {
                            localTaskId = taskRow.id;
                            setSubmittedTaskId(taskRow.id);
                        }
                        if (typeof taskRow?.progress === 'number' && taskRow.progress > 0)
                            setSubmitProgress(p => Math.max(p, taskRow.progress));
                        if (typeof taskRow?.status === 'string' && taskRow.status)
                            setSubmitStep(taskRow.status);
                    }

                    // Try fetching matches
                    const matchQuery = (supabase as any)
                        .from('matches')
                        .select(MATCH_ROW_SELECT)
                        .order('match_score', { ascending: false });
                    const { data: matchData } = await (localTaskId
                        ? matchQuery.eq('matching_task_id', localTaskId)
                        : matchQuery.eq('mission_id', missionId));

                    if (matchData && matchData.length > 0) {
                        results = matchData.map(mapMatchRow);
                        break;
                    }

                    await new Promise<void>(r => setTimeout(r, RESULTS_POLL_MS));
                }
            } finally {
                clearInterval(progressTick);
            }

            setMatchResults(results);
            setSubmittedMissionId(missionId);

            // Poll toutes les 4s pour rafraîchir les résultats jusqu'à complétion de la tâche
            if (localTaskId) {
                const { data: finalTask } = await supabase
                    .from('matching_tasks')
                    .select('status')
                    .eq('id', localTaskId)
                    .single();
                const finalStatus = (finalTask as any)?.status ?? '';
                const isTerminal = isTerminalTaskStatus(finalStatus);
                if (!isTerminal) {
                    console.log('[Matching] runMatching: task not terminal, polling for task', localTaskId);
                    setIsLiveUpdating(true);
                    const capturedTaskId = localTaskId;
                    pollRef.current = setInterval(async () => {
                        try {
                            const [{ data: freshMatches }, { data: freshTask }] = await Promise.all([
                                (supabase as any)
                                    .from('matches')
                                    .select(MATCH_ROW_SELECT)
                                    .eq('matching_task_id', capturedTaskId)
                                    .order('match_score', { ascending: false }),
                                supabase
                                    .from('matching_tasks')
                                    .select('status, progress')
                                    .eq('id', capturedTaskId)
                                    .single(),
                            ]);
                            if (freshMatches && freshMatches.length > 0) {
                                console.log('[Matching] poll update:', freshMatches.length, 'matches');
                                setMatchResults((freshMatches as any[]).map(mapMatchRow));
                            }
                            const status = (freshTask as any)?.status ?? '';
                            const progress = (freshTask as any)?.progress;
                            if (status) setSubmitStep(status);
                            if (typeof progress === 'number' && progress > 0) setSubmitProgress(progress);
                            if (isTerminalTaskStatus(status)) {
                                console.log('[Matching] task terminal, stopping poll');
                                setIsLiveUpdating(false);
                                clearPoll();
                            }
                        } catch (e) { console.warn('[Matching] poll error', e); }
                    }, 4000);
                } else {
                    console.log('[Matching] runMatching: task already terminal, no poll needed');
                }
            }

            const resultPath = localTaskId ? `/matching/${localTaskId}` : '/matching';
            notify('success', `Matching terminé — ${params.missionName}`, {
                description: results.length > 0
                    ? `${results.length} profil(s) classé(s) par pertinence.`
                    : 'Les résultats sont disponibles dans la fiche mission.',
                action: { label: 'Voir les résultats', path: resultPath, onClick: () => navigate(resultPath, { replace: true }) },
            });
            setViewState('submitted');
            navigate(resultPath);

        } catch (err) {
            clearPoll();
            cleanupChannel();
            if (err instanceof Error && err.message === 'TIMEOUT') {
                notify('info', 'Analyse toujours en cours', {
                    description: "Le workflow prend du temps. Consultez la fiche mission pour les résultats.",
                    action: { label: 'Voir les missions', path: '/missions', onClick: () => navigate('/missions') },
                });
                setViewState('submitted');
            } else {
                setSubmitError(
                    err instanceof Error ? err.message : 'Une erreur est survenue. Veuillez réessayer.'
                );
            }
        } finally {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
            setSubmitStep('');
        }
    }, [navigate, notify, cleanupChannel, clearPoll, org, user, raiseIfQuotaExceeded]);

    const loadFromTask = useCallback(async (taskId: number) => {
        try {
            const { data: taskData, error: taskError } = await supabase
                .from('matching_tasks')
                .select('mission_id, status, progress')
                .eq('id', taskId)
                .single();
            if (taskError || !taskData) return;

            const missionId: number = taskData.mission_id;
            setSubmittedMissionId(missionId);
            setSubmittedMissionSourceId(String(missionId));
            setSubmittedTaskId(taskId);

            const { data: matchData, error: matchError } = await (supabase as any)
                .from('matches')
                .select(MATCH_ROW_SELECT)
                .eq('matching_task_id', taskId)
                .order('match_score', { ascending: false });
            if (matchError) return;

            const results: MatchResult[] = (matchData || []).map(mapMatchRow);

            const isTerminal = isTerminalTaskStatus(taskData.status);

            if (results.length === 0) {
                // Le moteur écrit "error: <détail>" — jamais 'error' seul (fix P0.6)
                if ((taskData.status ?? '').startsWith('error')) {
                    setSubmitError("L'analyse a échoué (modèle IA indisponible). Relancez le matching depuis la fiche mission.");
                } else if (isTerminal) {
                    // terminal + 0 résultats = pas de match (pas d'erreur à afficher)
                } else {
                    // still in progress — ne pas afficher d'erreur, les résultats arrivent
                }
            }

            setMatchResults(results);
            setViewState('submitted');

            // Poll toutes les 4s pour rafraîchir les résultats jusqu'à complétion de la tâche
            if (!isTerminal) {
                console.log('[Matching] loadFromTask: task not terminal, polling for task', taskId);
                // Seed progress bar with current task state immediately
                if (taskData.status) setSubmitStep(taskData.status);
                if (typeof taskData.progress === 'number' && taskData.progress > 0) setSubmitProgress(taskData.progress);
                clearPoll();
                setIsLiveUpdating(true);
                pollRef.current = setInterval(async () => {
                    try {
                        const [{ data: freshMatches }, { data: freshTask }] = await Promise.all([
                            (supabase as any)
                                .from('matches')
                                .select(MATCH_ROW_SELECT)
                                .eq('matching_task_id', taskId)
                                .order('match_score', { ascending: false }),
                            supabase
                                .from('matching_tasks')
                                .select('status, progress')
                                .eq('id', taskId)
                                .single(),
                        ]);
                        if (freshMatches && freshMatches.length > 0) {
                            console.log('[Matching] poll update:', freshMatches.length, 'matches');
                            setMatchResults((freshMatches as any[]).map(mapMatchRow));
                        }
                        const status = (freshTask as any)?.status ?? '';
                        const progress = (freshTask as any)?.progress;
                        if (status) setSubmitStep(status);
                        if (typeof progress === 'number' && progress > 0) setSubmitProgress(progress);
                        if (isTerminalTaskStatus(status)) {
                            console.log('[Matching] task terminal, stopping poll');
                            setIsLiveUpdating(false);
                            clearPoll();
                        }
                    } catch (e) { console.warn('[Matching] poll error', e); }
                }, 4000);
            }
        } catch {
            // Ignore history refresh errors; polling/realtime will retry on the next update.
        }
    }, [clearPoll]);

    const reset = useCallback(() => {
        clearPoll();
        cleanupChannel();
        isSubmittingRef.current  = false;
        setViewState('setup');
        setMatchResults([]);
        setMissionHistory([]);
        setSubmitError(null);
        setSubmittedMissionId(null);
        setSubmittedMissionSourceId('');
        setSubmitProgress(0);
        setSubmitStep('');
        setSubmittedTaskId(null);
        setIsLiveUpdating(false);
    }, [clearPoll, cleanupChannel]);

    const clearError = useCallback(() => setSubmitError(null), []);

    return (
        <MatchingContext.Provider value={{
            viewState,
            matchResults,
            missionHistory,
            isSubmitting,
            submitStep,
            submitProgress,
            submitError,
            submittedMissionId,
            submittedMissionSourceId,
            submittedMatchingMode,
            submittedTaskId,
            isLiveUpdating,
            runMatching,
            loadFromTask,
            loadMissionHistory,
            reset,
            clearError,
        }}>
            {children}
        </MatchingContext.Provider>
    );
};

export const useMatching = () => {
    const ctx = useContext(MatchingContext);
    if (!ctx) throw new Error('useMatching must be used within MatchingProvider');
    return ctx;
};
